import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import DatePicker from "@/components/DatePicker";
import ContractorSelect from "@/components/ContractorSelect";
import AgreementTypeSelect from "@/components/AgreementTypeSelect";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useEffect, useState, useCallback } from "react";
import SaveButton from "@/components/SaveButton";
import {
  WriteAgreement,
  ReadAgreement,
  AgreementFormData,
} from "@/types/agreement";
import { useQueryClient } from "@tanstack/react-query";
import { AgreementType } from "@/types/agreementType";
import { useNavigate } from "react-router-dom";
import { Module, PATH_AGREEMENT } from "@/constants";
import Form from "@/components/Form";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { AgreementsService } from "@/services/agreements";
import { zodResolver } from "@hookform/resolvers/zod";
import { agreementFormSchema, writeAgreement } from "@/schemas/agreement";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useToast } from "@/components/ui/use-toast";
import { ApiError } from "@/services/api";

interface AgreementCreatorFormProps {
  inDialog?: boolean;
  contractorId?: string;
  contractorFullName?: string;
  actions?: React.ReactNode[];
}

/**
 * Shareable agreement creator form component that can be used in any place in the application.
 */
export const AgreementCreatorForm = ({
  inDialog = false,
  contractorId,
  contractorFullName,
  actions,
}: AgreementCreatorFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can_create } = useModulePermissions();

  const mutation = useMutationWithErrorHandling<
    ReadAgreement,
    ApiError,
    WriteAgreement,
    unknown
  >({
    mutationFn: AgreementsService.post,
    errorTitle: t("agreements.errors.create"),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [Module.Agreements] });
      toast({
        title: t("agreements.success.create"),
      });
      navigate(PATH_AGREEMENT.replace(":agreementId", data.id));
    },
    onError: (error) => {
      toast({
        title: t("agreements.errors.create"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const [selectedAgreementType, setSelectedAgreementType] = useState<
    AgreementType | undefined
  >();

  const form = useForm<AgreementFormData>({
    resolver: zodResolver(agreementFormSchema),
    defaultValues: {
      type_id: "",
      contractor_id: contractorId || "",
      start_date: "",
      agreementType: undefined,
    },
    disabled: !can_create || mutation.isPending,
  });

  useEffect(() => {
    if (contractorId) {
      form.setValue("contractor_id", contractorId);
    }
  }, [contractorId, form]);

  useEffect(() => {
    form.setValue("agreementType", selectedAgreementType);
  }, [selectedAgreementType, form]);

  const handleSubmit = useCallback(
    (data: AgreementFormData) => {
      const body = writeAgreement.parse(data);
      mutation.mutate(body);
    },
    [mutation],
  );

  useFormUnsavedChangesWarning(form);

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <Fieldset
        className={
          inDialog
            ? "gap-4"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        }
      >
        <Field
          name="type_id"
          label={t("agreements.fields.type_id_label")}
          required={true}
          render={({ field }) => (
            <AgreementTypeSelect
              {...field}
              className="w-full"
              showLink={false}
              inDialog={inDialog}
              onChange={(value, selectedItem) => {
                field.onChange(value);
                setSelectedAgreementType(selectedItem);
              }}
            />
          )}
        />
        <Field
          name="contractor_id"
          label={t("agreements.fields.contractor_id_label")}
          required={true}
          render={({ field }) => (
            <ContractorSelect
              {...field}
              className="w-full"
              showLink={false}
              inDialog={inDialog}
              disabled={field.disabled || !!contractorId}
              defaultItem={{ id: contractorId, name: contractorFullName }}
            />
          )}
        />
        <Field
          name="start_date"
          label={t("agreements.fields.start_date")}
          required={true}
          render={({ field }) => (
            <DatePicker className="w-full" {...field} inDialog={inDialog} />
          )}
        />
      </Fieldset>
      <div className="flex justify-end items-center mt-4 gap-4">
        {actions}
        <SaveButton
          disabled={!can_create}
          isLoading={mutation.isPending}
          label={t("agreements.createForm.createButton")}
          loadingLabel={t("submitButton.creating")}
        />
      </div>
    </Form>
  );
};
